import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown, Coffee, Heart } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SubmissionForm, { SubmissionSuccess } from '../components/coffee/SubmissionForm';
import LiveWall from '../components/coffee/LiveWall';
import Constellation from '../components/coffee/Constellation';
import {
  fetchPublicSubmissions,
  fetchSubmissionCount,
  fetchProjectStats,
  fetchFeaturedSubmissions,
  type CoffeeSubmission,
  type CoffeeProjectStats,
  statusLabel,
  statusColor,
  displayNames,
} from '../lib/coffee-project';

export default function CoffeeProject() {
  const [submissions, setSubmissions] = useState<CoffeeSubmission[]>([]);
  const [featured, setFeatured] = useState<CoffeeSubmission[]>([]);
  const [count, setCount] = useState(127);
  const [stats, setStats] = useState<CoffeeProjectStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState<CoffeeSubmission | null>(null);

  const goal = 1000;

  const load = useCallback(async () => {
    try {
      const [subs, c, st, feat] = await Promise.all([
        fetchPublicSubmissions(),
        fetchSubmissionCount(),
        fetchProjectStats(),
        fetchFeaturedSubmissions(),
      ]);
      setSubmissions(subs);
      setCount(Math.max(c, subs.length));
      setStats(st);
      setFeatured(feat);
    } catch {
      // keep defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'The Coffee You Keep Meaning to Have | Bond Coffee';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', "There's someone you've been meaning to have coffee with. Bond Coffee is trying to make 1,000 of those coffees actually happen. Add yours to the experiment.");
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', 'Who have you been meaning to have coffee with?');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', "We're trying to make 1,000 of those coffees actually happen.");
    return () => {
      document.title = 'Bond Coffee Roasters';
    };
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmitted = (s: CoffeeSubmission) => {
    setSubmitted(s);
    setCount((c) => c + 1);
    if (s.is_public) {
      setSubmissions((prev) => [...prev, s]);
    }
  };

  const scrollToForm = () => {
    document.getElementById('submit-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToWall = () => {
    document.getElementById('wall-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const happenedCount = submissions.filter((s) => s.status === 'happened').length;
  const scheduledCount = submissions.filter((s) => s.status === 'scheduled').length;

  return (
    <div className="min-h-screen bg-[#0a0f18] text-white">
      <Header />

      {/* 1. HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#132947]/40 via-[#0a0f18] to-[#0a0f18]" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#fda36b]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#132947]/30 rounded-full blur-3xl" />

        <div className="relative max-w-5xl mx-auto px-6 py-32 text-center space-y-8">
          <div className="space-y-6">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter leading-[0.95]">
              Who have you been meaning to have <span className="text-[#fda36b]">coffee</span> with?
            </h1>
            <p className="text-xl md:text-2xl text-white/70 font-light max-w-3xl mx-auto leading-relaxed">
              We're trying to get 1,000 of those coffees to actually happen.
            </p>
          </div>

          {/* Live counter */}
          <div className="inline-flex flex-col items-center gap-2 py-8">
            <div className="flex items-baseline gap-3">
              <span className="text-7xl md:text-8xl font-bold text-[#fda36b] tabular-nums tracking-tighter">
                {count}
              </span>
              <span className="text-3xl md:text-4xl text-white/40 font-light">/ {goal}</span>
            </div>
            <p className="text-white/40 text-sm uppercase tracking-widest font-medium">coffees</p>
            <div className="w-64 h-1 bg-white/10 rounded-full overflow-hidden mt-2">
              <div
                className="h-full bg-gradient-to-r from-[#fda36b] to-[#fda36b]/60 rounded-full transition-all duration-1000"
                style={{ width: `${Math.min((count / goal) * 100, 100)}%` }}
              />
            </div>
          </div>

          <div className="space-y-4 max-w-2xl mx-auto">
            <p className="text-white/50 leading-relaxed">
              Life gets busy. People move. Kids happen. Work happens. Years somehow disappear.
              And there's usually someone we keep saying we should see.
            </p>
            <p className="text-white/50 leading-relaxed">
              So we started a little experiment. Tell us who you've been meaning to have coffee with.
              Maybe we can help make it happen.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <button
              onClick={scrollToForm}
              className="group inline-flex items-center justify-center gap-2 bg-[#fda36b] text-[#132947] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#fda36b]/90 transition-all hover:scale-105"
            >
              I Have Someone
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={scrollToWall}
              className="inline-flex items-center justify-center gap-2 bg-white/5 text-white border border-white/15 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white/10 transition-all"
            >
              See the Coffees
              <ArrowDown className="w-5 h-5" />
            </button>
          </div>

          <p className="text-white/30 text-sm">
            No purchase. No catch. Just coffee with someone you should probably see.
          </p>
        </div>
      </section>

      {/* 2. WHAT IS THIS? */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold text-white/90 leading-tight">
            A social experiment involving coffee and unfinished conversations.
          </h2>
          <div className="space-y-4 text-white/60 leading-relaxed text-lg">
            <p>Bond Coffee started with a pretty simple belief:</p>
            <p className="text-white/80 font-medium">Coffee is rarely really about coffee.</p>
            <p>It's the excuse. To sit down. To catch up. To apologize. To laugh about something stupid that happened fifteen years ago. To finally call someone you keep saying you're going to call.</p>
            <p>So we're trying something. We're collecting 1,000 stories about people who have been meaning to have coffee together — and seeing how many we can turn into actual coffees.</p>
            <p>Some will be funny. Some will be awkward. Some will probably matter more than anyone expects.</p>
            <p className="text-white/80 font-medium">That's the experiment.</p>
          </div>
        </div>
      </section>

      {/* 3. SUBMISSION */}
      <section id="submit-section" className="py-24 px-6 border-t border-white/5">
        {submitted ? (
          <SubmissionSuccess
            submission={submitted}
            onTellUs={() => setSubmitted(null)}
          />
        ) : (
          <SubmissionForm onSubmitted={handleSubmitted} />
        )}
      </section>

      {/* 4. LIVE WALL */}
      <section id="wall-section" className="border-t border-white/5">
        <LiveWall submissions={submissions} />
      </section>

      {/* 5. LIVE ART / DATA */}
      <section className="py-24 px-6 border-t border-white/5 bg-[#0d121c]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-white/90 leading-tight mb-16 max-w-3xl">
            What happens when 1,000 people stop saying "we should get coffee sometime"?
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-16">
            <Stat value={count} label="People who finally named the person" />
            <Stat value={scheduledCount} label="Coffees scheduled" />
            <Stat value={happenedCount} label="Coffees happened" />
            <Stat value={stats?.longest_time_apart ?? '14 years'} label="Longest time apart" isText />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-16">
            <Stat value={stats?.shortest_distance ?? '11 minutes'} label="Shortest distance between two people who somehow still hadn't gotten together" isText />
            <Stat value="0" label="Good excuses found so far" />
          </div>

          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8">
            <p className="text-white/40 text-sm mb-4 font-medium uppercase tracking-wider">The Constellation</p>
            <Constellation submissions={submissions} />
            <p className="text-white/30 text-xs text-center mt-4">
              Every dot is a coffee. Every line is one that happened.
            </p>
          </div>
        </div>
      </section>

      {/* 6. FEATURED STORIES */}
      {featured.length > 0 && (
        <section className="py-24 px-6 border-t border-white/5">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-12">A Few Coffees</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {featured.map((s) => {
                const { label } = displayNames(s);
                return (
                  <div key={s.id} className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden hover:border-[#fda36b]/30 transition-all group">
                    {s.photo_url && (
                      <div className="aspect-[4/3] overflow-hidden bg-white/5">
                        <img src={s.photo_url} alt={label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    )}
                    <div className="p-6 space-y-4">
                      <h3 className="text-2xl font-bold">{label}</h3>
                      <p className="text-[#fda36b] font-semibold">{s.time_since}</p>
                      {s.featured_story && (
                        <p className="text-white/60 leading-relaxed italic">"{s.featured_story}"</p>
                      )}
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${statusColor(s.status)}`}>
                        {statusLabel(s.status)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 7. THE QUESTION */}
      <section className="py-32 px-6 border-t border-white/5 text-center">
        <div className="max-w-4xl mx-auto space-y-8">
          <h2 className="text-4xl md:text-7xl font-bold tracking-tighter leading-tight">
            Who came to mind when you read this page?
          </h2>
          <p className="text-2xl text-[#fda36b] font-light">That's probably the person.</p>
          <button
            onClick={scrollToForm}
            className="inline-flex items-center gap-2 bg-[#fda36b] text-[#132947] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#fda36b]/90 transition-all hover:scale-105"
          >
            Tell Us About Them <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* 8. THE BOND CONNECTION */}
      <section className="py-24 px-6 border-t border-white/5 bg-[#0d121c]">
        <div className="max-w-3xl mx-auto space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold text-white/90">Why is a coffee company doing this?</h2>
          <div className="space-y-4 text-white/60 leading-relaxed text-lg">
            <p>We're Bond Coffee. We roast coffee in Marblehead, Massachusetts.</p>
            <p>But the thing we've always liked most about coffee isn't really the coffee. It's what tends to happen around it.</p>
            <p className="text-white/80">Stories. Arguments. Bad jokes. Good news. Old friends. New ideas. People actually sitting across from each other.</p>
            <p>So this felt like something worth trying.</p>
          </div>
          <div className="pt-4">
            <p className="text-white/70 text-lg mb-4">If you need coffee for yours, we happen to know some people.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#fda36b] hover:gap-3 transition-all text-lg font-semibold"
            >
              Visit Bond Coffee <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* 9. MANIFESTO */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <h2 className="text-4xl md:text-6xl font-bold tracking-tighter">The rules are simple.</h2>
          <div className="space-y-3 text-white/60 text-lg leading-relaxed pt-4">
            <p>Have coffee with someone.</p>
            <p>Phones can stay on the table.</p>
            <p>It doesn't have to be profound.</p>
            <p>You don't have to fix anything.</p>
            <p>You don't even have to drink coffee.</p>
            <p className="text-white font-medium text-xl pt-4">Just show up.</p>
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA */}
      <section className="py-32 px-6 border-t border-white/5 text-center bg-gradient-to-b from-transparent to-[#132947]/30">
        <div className="max-w-3xl mx-auto space-y-8">
          <h2 className="text-4xl md:text-7xl font-bold tracking-tighter leading-tight">
            Someone is waiting for that text.
          </h2>
          <button
            onClick={scrollToForm}
            className="inline-flex items-center gap-2 bg-[#fda36b] text-[#132947] px-10 py-5 rounded-xl font-bold text-xl hover:bg-[#fda36b]/90 transition-all hover:scale-105"
          >
            Put Your Coffee On The Board <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-white/40 text-lg">1,000 coffees. One at a time.</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Stat({ value, label, isText }: { value: number | string; label: string; isText?: boolean }) {
  return (
    <div className="space-y-2">
      <p className={`text-5xl md:text-6xl font-bold text-[#fda36b] tracking-tighter ${isText ? 'text-3xl md:text-4xl' : 'tabular-nums'}`}>
        {value}
      </p>
      <p className="text-white/50 text-sm leading-snug">{label}</p>
    </div>
  );
}
